import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useCategories } from '../hooks/useCategories';
import { formatDateRange } from '../lib/dateUtils';
import './CategoryPage.css';

export default function CategoryPage({ user, onNavigate, refreshSignal }) {
  const { categories } = useCategories(user.id);
  const [records, setRecords] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);

  useEffect(() => {
    const fetchRecords = async () => {
      const { data } = await supabase
        .from('records')
        .select('*')
        .eq('user_id', user.id);
      setRecords(data || []);
    };
    fetchRecords();
  }, [user.id, refreshSignal]);

  const countByName = records.reduce((acc, r) => {
    const name = r.category_name ?? '';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

  const filteredRecords = selectedCat
    ? records
        .filter((r) => r.category_name === selectedCat.name)
        .sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? ''))
    : [];

  // ── 필터 뷰 ──────────────────────────────────────
  if (selectedCat) {
    return (
      <div className="category-page">
        <header className="cat-header">
          <button className="cat-header-back" onClick={() => setSelectedCat(null)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="cat-header-title">{selectedCat.name}</span>
          <div style={{ width: 36 }} />
        </header>

        <div className="cat-record-list">
          {filteredRecords.length === 0 && (
            <p className="cat-empty">이 카테고리에 기록이 없어요.</p>
          )}
          {filteredRecords.map((record) => (
            <div
              key={record.id}
              className="record-card"
              role="button"
              onClick={() => onNavigate('editRecord', record)}
            >
              <div className="record-card-body">
                <p className="record-title">{record.title}</p>
                <p className="record-meta">
                  {formatDateRange(record.start_date, record.end_date, record.is_ongoing)}
                </p>
                {record.memo && <p className="record-memo">{record.memo}</p>}
              </div>
              {record.photos?.length > 0 && (
                <div className="record-thumbnail">
                  <img src={record.photos[0]} alt="썸네일" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── 카테고리 그리드 뷰 ───────────────────────────
  return (
    <div className="category-page">
      <header className="cat-header">
        <span className="cat-header-title-main">카테고리</span>
        <button className="cat-manage-btn" onClick={() => onNavigate('categoryManage')}>
          관리
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </header>

      <div className="cat-grid">
        {categories.map((cat) => (
          <button
            key={cat.id}
            className="cat-grid-item"
            onClick={() => setSelectedCat(cat)}
          >
            {cat.icon && <span className="cat-grid-emoji">{cat.icon}</span>}
            <span className="cat-grid-name">{cat.name}</span>
            <span className="cat-grid-count">{countByName[cat.name] ?? 0}개</span>
          </button>
        ))}
      </div>
    </div>
  );
}
